// H2.3 WO#5 §WHEEL-ROUTER — pure module that owns the "wheel event →
// zoom vs page-scroll" decision AND the screen-anchored camera math
// used by the GridViewer's native wheel listener.
//
// Extraction rationale:
//   The wheel handler previously lived inline inside a React effect
//   in GridViewer.tsx. Vitest cannot exercise a React-inline handler
//   without mounting the full component; that made the behaviour a
//   pure runtime observation. WO#5 §regression-guard requires that
//   plain-wheel MUST scroll the page (no preventDefault, no scale
//   change) and Ctrl/Meta+wheel MUST zoom around the pointer anchor
//   via `clampCamera(zoomAt(...))`. Isolating the decision here lets
//   the guard live as a pure vitest, plus a jsdom synthetic-event
//   test around a stub element that reproduces the exact browser
//   binding (`addEventListener('wheel', ..., { passive: false })`).
//
// SEMANTICS PRESERVED VERBATIM:
//   Every input (ctrl/meta gating, delta normalisation for
//   deltaMode=DOM_DELTA_LINE, exp(-Δ * 0.0015) exponential zoom
//   factor, screen anchor derived from `wrap.getBoundingClientRect`)
//   is reproduced 1:1 from the pre-extraction GridViewer.tsx block.
//   The re-attach helper below preserves { passive:false } so
//   `event.preventDefault()` still works on the zoom path.
import { clampCamera, zoomAt, type Camera } from "./camera";

export interface WheelIntent {
  /** True when the browser expects a camera zoom (Ctrl on all OS,
   * Meta/⌘ on macOS, and — as a side-effect — every macOS pinch-zoom
   * event which the browser synthesises with `ctrlKey=true`). */
  zoom: boolean;
}

/** Pure classifier: decides whether a wheel event should zoom the
 *  Grid or fall through to native document scrolling. */
export function classifyWheelIntent(e: {
  ctrlKey: boolean;
  metaKey: boolean;
}): WheelIntent {
  return { zoom: e.ctrlKey === true || e.metaKey === true };
}

/** Pure camera-update: given a wheel event on a wrap of `rect`
 *  dimensions and the current camera, return the new camera. This
 *  is the *only* place the exponential factor lives; callers must
 *  route every zoom-capable wheel event through this function so
 *  clamp/anchor semantics stay consistent. */
export function computeWheelZoom(
  cam: Camera,
  wrapRect: { left: number; top: number; width: number; height: number },
  e: {
    clientX: number;
    clientY: number;
    deltaMode: number;
    deltaY: number;
  },
): Camera {
  const sx = e.clientX - wrapRect.left;
  const sy = e.clientY - wrapRect.top;
  // DOM_DELTA_LINE (deltaMode === 1) reports whole lines; the
  // browser tail normalises them at ~16 CSS px per line. Everything
  // else (DOM_DELTA_PIXEL = 0, DOM_DELTA_PAGE = 2) we treat as CSS
  // pixels — Page is unusual on wheel but wouldn't zoom anyway.
  const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
  const factor = Math.exp(-delta * 0.0015);
  const newScale = cam.scale * factor;
  return clampCamera(
    zoomAt(cam, sx, sy, newScale),
    wrapRect.width,
    wrapRect.height,
  );
}

/**
 * Attaches the canonical wheel routing to a DOM element. Returns a
 * detach function.
 *
 * Contract (this is the WO#5 P0 regression the tests guard):
 *
 *   plain wheel (no ctrl/meta)  →  intent.zoom=false
 *                                  handler returns EARLY
 *                                  preventDefault() NOT called
 *                                  camera untouched
 *                                  page scrolls natively
 *
 *   Ctrl/Meta + wheel           →  intent.zoom=true
 *                                  preventDefault() IS called
 *                                  camera updated via computeWheelZoom
 *                                  callbacks fire (mark authority,
 *                                  schedule tile refresh, mark dirty)
 *
 * The wheel listener is registered with `{ passive:false }` on
 * purpose — a passive listener MAY NOT call preventDefault(). Losing
 * this flag would silently break the zoom path (the browser would
 * scroll the page instead of zooming even with Ctrl). A dedicated
 * test asserts the attach call re-registers with this option.
 */
export interface AttachWheelRoutingOptions {
  /** Read the CURRENT camera. Called on each wheel tick. */
  getCamera: () => Camera;
  /** Persist a new camera. Called only on the zoom path. */
  setCamera: (next: Camera) => void;
  /** Called after a zoom updates the camera. Wire this to the
   *  viewer's "user took camera authority" flag. */
  onUserCameraAuthority?: () => void;
  /** Called after a zoom to trigger the next render frame. */
  onDirty?: () => void;
  /** Called after a zoom to schedule a visible-tile refresh. */
  onScheduleRefresh?: () => void;
}

export function attachWheelRouting(
  wrap: HTMLElement,
  opts: AttachWheelRoutingOptions,
): () => void {
  const onWheel = (e: WheelEvent) => {
    const intent = classifyWheelIntent(e);
    if (!intent.zoom) {
      // Page-scroll path. DO NOT preventDefault. DO NOT touch camera.
      return;
    }
    e.preventDefault();
    const rect = wrap.getBoundingClientRect();
    const next = computeWheelZoom(opts.getCamera(), rect, e);
    opts.setCamera(next);
    opts.onUserCameraAuthority?.();
    opts.onDirty?.();
    opts.onScheduleRefresh?.();
  };
  wrap.addEventListener("wheel", onWheel, { passive: false });
  return () => wrap.removeEventListener("wheel", onWheel);
}
